// Main Minew Testing UI Component

import React, { useCallback } from 'react';
import Icon from '../../core/Icon';
import { useMinewData, useSimulation, usePatientCreation, useStaffBeaconInit } from './hooks';
import {
  StatusBar,
  Controls,
  BeaconPanel,
  RoomPanel,
  SensorPanel,
  InstructionsBox,
  StatusMessageBox,
  WarningBox,
} from './components';
import { Content, Header, Title, Grid } from './styles';
import { generateGatewayId } from './utils';
import { DEFAULT_GATEWAY_THRESHOLD } from './constants';
import type { Room } from './types';

interface MinewTestUIProps {
  match?: {
    params: {
      organizationId?: string;
    };
  };
}

export const MinewTestUI: React.FC<MinewTestUIProps> = ({ match }) => {
  const organizationId = match?.params?.organizationId;

  // Data hook
  const {
    rooms,
    roomsByType,
    existingTestGateways,
    existingTestBracelets,
    patientsWithTestBracelets,
    beaconToPatientMap,
    beaconToStaffMap,
    beaconToPhysicianMap,
    beaconToAnesthesiologistMap,
    physicians,
    procedureTypes,
    staff,
    anesthesiologists,
    isLoading,
    editGateway,
    createPatient,
    editStaff,
    editPhysician,
    editAnesthesiologist,
    refetchGateways,
    refetchPatients,
    refetchStaff,
    refetchPhysicians,
    refetchAnesthesiologists,
  } = useMinewData();

  // Simulation hook
  const {
    isRunning,
    signalStrength,
    pingInterval,
    selectedBeacon,
    beaconLocations,
    gatewayRoomMap,
    lastSync,
    syncCount,
    sensorStates,
    setIsRunning,
    setSignalStrength,
    setPingInterval,
    setSelectedBeacon,
    moveBeaconToRoom,
    clearAllLocations,
    sendSignals,
    updateSensorState,
    getBeaconLocationDisplay,
  } = useSimulation(organizationId, rooms);

  // Patient creation hook
  const { creatingPatients, patientCreationStatus, createTestPatientsWithBracelets } = usePatientCreation({
    physicians,
    procedureTypes,
    beaconToPatientMap,
    createPatient,
    refetchPatients,
  });

  // Staff/Physician beacon initialization hook
  const { initializingStaffBeacons, staffBeaconStatus, initializeStaffBeacons } = useStaffBeaconInit({
    staff,
    physicians,
    anesthesiologists,
    editStaff,
    editPhysician,
    editAnesthesiologist,
    refetchStaff,
    refetchPhysicians,
    refetchAnesthesiologists,
  });

  // Initialize all test gateways with room assignments
  const initializeGateways = useCallback(async () => {
    const promises: Promise<unknown>[] = [];

    rooms.forEach(room => {
      const gwId = generateGatewayId(room);
      const existingGw = existingTestGateways[gwId];

      // Only assign room if gateway exists but has no room, or if we need to create it
      if (!existingGw || !existingGw.room) {
        promises.push(
          editGateway({
            variables: {
              id: gwId,
              gatewayType: 'GATEWAY',
              roomId: room.id,
              threshold: DEFAULT_GATEWAY_THRESHOLD,
            },
          }).catch(err => console.warn(`Failed to setup gateway ${gwId}:`, err))
        );
      }
    });

    if (promises.length > 0) {
      await Promise.all(promises);
      // Send initial signals to create gateways
      await sendSignals();
      // Refetch to see updated state
      setTimeout(() => {
        refetchGateways();
      }, 2000);
    }
  }, [rooms, existingTestGateways, editGateway, sendSignals, refetchGateways]);

  // Handle room click
  const handleRoomClick = useCallback(
    (room: Room) => {
      if (selectedBeacon) {
        moveBeaconToRoom(selectedBeacon, room);
      }
    },
    [selectedBeacon, moveBeaconToRoom]
  );

  // Handle move offsite
  const handleMoveOffsite = useCallback(
    (beaconId: string) => {
      moveBeaconToRoom(beaconId, null);
    },
    [moveBeaconToRoom]
  );

  // Loading state
  if (isLoading) {
    return (
      <Content>
        <Header>
          <Title>
            <Icon className="material-icons">router</Icon>
            Loading...
          </Title>
        </Header>
      </Content>
    );
  }

  return (
    <Content>
      <Header>
        <Title>
          <Icon className="material-icons">router</Icon>
          Beacon Signal Simulator
        </Title>
        <StatusBar
          roomCount={rooms.length}
          testGatewayCount={Object.keys(existingTestGateways).length}
          testBraceletCount={existingTestBracelets.size}
          patientsWithBraceletsCount={patientsWithTestBracelets.length}
          syncCount={syncCount}
          lastSync={lastSync}
        />
      </Header>

      <InstructionsBox />

      {patientCreationStatus && <StatusMessageBox message={patientCreationStatus} />}

      {staffBeaconStatus && <StatusMessageBox message={staffBeaconStatus} />}

      <WarningBox />

      <Controls
        isRunning={isRunning}
        signalStrength={signalStrength}
        pingInterval={pingInterval}
        creatingPatients={creatingPatients}
        initializingStaffBeacons={initializingStaffBeacons}
        onToggleSimulation={() => setIsRunning(!isRunning)}
        onSendOnce={sendSignals}
        onInitializeGateways={initializeGateways}
        onCreatePatients={createTestPatientsWithBracelets}
        onInitializeStaffBeacons={initializeStaffBeacons}
        onClearLocations={clearAllLocations}
        onSignalStrengthChange={setSignalStrength}
        onPingIntervalChange={setPingInterval}
      />

      <Grid>
        <BeaconPanel
          organizationId={organizationId || ''}
          selectedBeacon={selectedBeacon}
          beaconToPatientMap={beaconToPatientMap}
          beaconToStaffMap={beaconToStaffMap}
          beaconToPhysicianMap={beaconToPhysicianMap}
          beaconToAnesthesiologistMap={beaconToAnesthesiologistMap}
          gatewayRoomMap={gatewayRoomMap}
          onSelectBeacon={setSelectedBeacon}
          onMoveOffsite={handleMoveOffsite}
          getBeaconLocationDisplay={getBeaconLocationDisplay}
        />

        <RoomPanel
          roomsByType={roomsByType}
          selectedBeacon={selectedBeacon}
          existingTestGateways={existingTestGateways}
          beaconLocations={beaconLocations}
          onRoomClick={handleRoomClick}
        />
      </Grid>

      <SensorPanel sensorStates={sensorStates} onUpdateSensor={updateSensorState} />
    </Content>
  );
};

export default MinewTestUI;
